// Card 62, the neat line (R802, ui-map-interface.md §17.3.62).
//
// Mockup round thirty-six, card 03, "the neat line and its graduation".
// The neat line is the rule that closes a chart, and the graduation beside
// it counts the degrees. Here it counts CELLS: one block per row on the two
// side edges and one per column on the top and the bottom edge, with a
// figure every `label_every` cells. The figure is the latitude of the row
// or the longitude of the column in the chart frame (R2742), the figure the
// graticule (card 05) and the border band (card 39) print, so the three
// pieces agree on every figure, and every peek states its position in the
// same figures.
//
// **A FIGURE STANDS ON THE TICK WHERE ITS DIVISION STARTS** (R805). A
// chart's figure names a graduation line, and the bar after the line is the
// division it opens: the figure is never set in the middle of a bar. The
// divisions are the border band's (card 39) and the graticule's (card 05):
// column c runs from `c·COL_PITCH` to `(c+1)·COL_PITCH`, and row r from the
// centre line of row r to the centre line of row r+1. The figure of column
// c is centred on the tick at `c·COL_PITCH`, and the figure of row r stands
// on the centre line of row r. A short lead-in block runs from the world's
// top edge to the line of row 0, as the band's does.
//
// The historical reading is a double rule with filled blocks between the
// two rules in turn; the modern one is a single rule with the filled and
// hollow bar and a tick. Both write the degrees and the minutes, as an old
// sheet did.
//
// **It is STATIC, and it is the sheet.** The graduation is a function of the
// grid, so the piece packs on the hello, on a reading change and when the
// Fell face lands, and a replay film keeps it. It culls nothing on the CPU,
// for the reason card 61 gives: a static pack outlives every pan.
//
// **The figures wait for `label_zoom_min`.** Under it a world-space figure
// is too small to read. A static piece repacks on a zoom-band crossing and
// on nothing finer, so the threshold is a band edge another piece holds,
// and the piece answers `zoomband`. The test pins the pair.

import { colLon, latFig, lonFig, rowLat } from '../../../ui/chartpos';
import type { Anchor } from '../../../render/text-run';
import { COLS, ROWS, WORLD, WORLD_H } from '../../../world';
import type { FrameInfo } from '../../layer';
import type { SegmentSink } from '../../layers/batches';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { stepPx } from '../steps';
import { EDGES, type Edge } from './margin-warning';
import { CHART_INK, colLine, faceOf, inkOf, rowY } from './shared';

/** A side edge counts rows; the top and the bottom edge count columns. */
function isSide(e: Edge): boolean {
  return e === 'left' || e === 'right';
}

/**
 * The blocks along one edge: the span of each in px along it, and its
 * number. Block n starts on the graduation line of its first cell (R805).
 *
 * A block counts `per` cells. The last block runs on to the end of the
 * edge, and on a side edge a lead-in block numbered -1 runs from the top
 * of the world to the line of row 0, so the graduation leaves no gap at a
 * corner. A block is filled when its number is even.
 */
export function blockSpans(side: boolean, per: number): { n: number; s0: number; s1: number }[] {
  const k = Math.max(1, Math.floor(per));
  const out: { n: number; s0: number; s1: number }[] = [];
  if (side) {
    out.push({ n: -1, s0: 0, s1: rowY(0) });
    for (let r = 0, n = 0; r < ROWS; r += k, n++) {
      out.push({ n, s0: rowY(r), s1: r + k >= ROWS ? WORLD_H : rowY(r + k) });
    }
  } else {
    for (let c = 0, n = 0; c < COLS; c += k, n++) {
      out.push({ n, s0: colLine(c), s1: Math.min(WORLD, colLine(c + k)) });
    }
  }
  return out;
}

/** The cells that carry a figure along one edge, and where each stands. */
function figuresOf(side: boolean, every: number): { n: number; s: number }[] {
  const step = Math.max(1, Math.floor(every));
  const count = side ? ROWS : COLS;
  const out: { n: number; s: number }[] = [];
  for (let i = 0; i < count; i += step) out.push({ n: i, s: side ? rowY(i) : colLine(i) });
  return out;
}

/** A line parallel to an edge, `d` px outside it, from `s0` to `s1` along it. */
function strip(
  seg: SegmentSink, e: Edge, s0: number, s1: number, d: number,
  width: number, ink: number, alpha: number,
): void {
  if (e === 'top') seg.line(s0, -d, s1, -d, width, ink, alpha);
  else if (e === 'bottom') seg.line(s0, WORLD_H + d, s1, WORLD_H + d, width, ink, alpha);
  else if (e === 'left') seg.line(-d, s0, -d, s1, width, ink, alpha);
  else seg.line(WORLD + d, s0, WORLD + d, s1, width, ink, alpha);
}

/** A line across an edge at `s` along it, from `d0` to `d1` px outside it. */
function cross(
  seg: SegmentSink, e: Edge, s: number, d0: number, d1: number,
  width: number, ink: number, alpha: number,
): void {
  if (e === 'top') seg.line(s, -d0, s, -d1, width, ink, alpha);
  else if (e === 'bottom') seg.line(s, WORLD_H + d0, s, WORLD_H + d1, width, ink, alpha);
  else if (e === 'left') seg.line(-d0, s, -d1, s, width, ink, alpha);
  else seg.line(WORLD + d0, s, WORLD + d1, s, width, ink, alpha);
}

/** A rule closed round the world, `d` px outside every edge. */
function rule(seg: SegmentSink, d: number, width: number, ink: number, alpha: number): void {
  for (const e of EDGES) {
    strip(seg, e, -d, (isSide(e) ? WORLD_H : WORLD) + d, d, width, ink, alpha);
  }
}

export class NeatLinePiece implements Piece {
  readonly id = 'neat_line' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  readonly film = 'keep' as const;
  readonly rebuildOn = ['hello', 'reading', 'font', 'zoomband'] as const;

  /** Nothing is built ahead: the graduation is a function of the grid. */
  rebuild(_ctx: ChartContext): void { /* the grid is the whole state */ }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const t = ctx.cfg.pieces.neat_line;
    const f = t.fig;
    const hist = ctx.reading === 'historical';
    const r = t[ctx.reading];
    const ink = inkOf(r.ink, CHART_INK.pale);
    const seg = sinks.segments;
    const g = f.rule_gap;

    // The rule, and the blocks of the graduation.
    rule(seg, g, r.width, ink, r.alpha);
    if (hist) {
      const hf = t.historical.fig;
      rule(seg, g + hf.band_px, hf.outer_width, ink, hf.outer_alpha);
      for (const e of EDGES) {
        for (const { n, s0, s1 } of blockSpans(isSide(e), f.divisions_per_block)) {
          if (n % 2 === 0) strip(seg, e, s0, s1, g + hf.band_px / 2, hf.band_px, ink, hf.block_alpha);
        }
      }
    } else {
      const mf = t.modern.fig;
      const mid = (mf.bar_in + mf.bar_out) / 2;
      const bar = mf.bar_out - mf.bar_in;
      for (const e of EDGES) {
        for (const { n, s0, s1 } of blockSpans(isSide(e), f.divisions_per_block)) {
          if (n % 2 === 0) {
            strip(seg, e, s0, s1, mid, bar, ink, mf.fill_alpha);
            continue;
          }
          strip(seg, e, s0, s1, mf.bar_in, mf.hollow_width, ink, mf.hollow_alpha);
          strip(seg, e, s0, s1, mf.bar_out, mf.hollow_width, ink, mf.hollow_alpha);
          cross(seg, e, s0, mf.bar_in, mf.bar_out, mf.hollow_width, ink, mf.hollow_alpha);
          cross(seg, e, s1, mf.bar_in, mf.bar_out, mf.hollow_width, ink, mf.hollow_alpha);
        }
      }
    }

    // The figures, and the modern tick under each one.
    if (ctx.zoom < f.label_zoom_min) return;
    const rf = r.fig;
    const face = faceOf(ctx.reading);
    const step = t.text.main;
    const size = stepPx(face, step);
    for (const e of EDGES) {
      const side = isSide(e);
      for (const { n, s } of figuresOf(side, f.label_every)) {
        if (!hist) {
          const mf = t.modern.fig;
          cross(seg, e, s, mf.bar_out, mf.tick_out, mf.tick_width, ink, mf.tick_alpha);
        }
        const label = side ? latFig(rowLat(n)) : lonFig(colLon(n));
        // The figure stands `label_d` outside its edge: centred over a top
        // or a bottom tick, and set against the sheet beside a side one.
        const d = rf.label_d;
        // The band grows the figure about the end that faces the sheet, so
        // it never crosses its edge (R1796).
        let x = s, y = s, py = s;
        let anchor: Anchor = 'centre';
        if (e === 'top') { y = -(d + size / 2); py = -d; }
        else if (e === 'bottom') { y = WORLD_H + d + size / 2; py = WORLD_H + d; }
        else if (e === 'left') { x = -d; anchor = 'right'; }
        else { x = WORLD + d; anchor = 'left'; }
        ctx.glyphs.word(sinks.quads, label, x, y, face, step, ink, rf.label_alpha, { anchor, pivot: { x, y: py } });
      }
    }
  }
}

/** The registry's entry. */
export const neatLine = new NeatLinePiece();
